import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import MetaPixelTracker from './components/MetaPixelTracker';
import OfflineBanner from './components/OfflineBanner';
import ImpersonationBanner from './components/ImpersonationBanner';
import SandboxBanner from './components/SandboxBanner';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/toaster';
import { ConfirmProvider } from './hooks/useConfirm';
import ProtectedRoute from './components/ProtectedRoute';
import CookieConsentBanner from './components/shared/CookieConsentBanner';
import { SHOW_INDIVIDUAL } from './utils/featureFlags';

// Public Pages
import Home from './components/HeroSection';
import Header from './components/Header';
import Footer from './components/Footer';

// Wave 42.364 — these were eagerly bundled into the main chunk even
// though each is only ever visited on its own dedicated route. Making
// them lazy shrinks the JS every visitor downloads on first load.
const AdminLogin = React.lazy(() => import('./pages/AdminLogin'));
const CommonLogin = React.lazy(() => import('./pages/CommonLogin'));
const StaffLogin = React.lazy(() => import('./pages/StaffLogin'));
const AmbassadorLogin = React.lazy(() => import('./pages/ambassador/AmbassadorLogin'));
const AmbassadorSetPassword = React.lazy(() => import('./pages/ambassador/AmbassadorSetPassword'));
const AmbassadorPortal = React.lazy(() => import('./pages/ambassador/AmbassadorPortal'));
const AmbassadorLeadsPage = React.lazy(() => import('./pages/dashboards/AmbassadorLeadsPage'));
const AmbassadorBlitzPage = React.lazy(() => import('./pages/dashboards/AmbassadorBlitzPage'));
const BusinessCallsPage = React.lazy(() => import('./pages/dashboards/BusinessCallsPage'));
const AmbassadorLeaderboardPage = React.lazy(() => import('./pages/ambassador/AmbassadorLeaderboardPage'));
const AmbassadorResourcesPage = React.lazy(() => import('./pages/ambassador/AmbassadorResourcesPage'));
const SignupSelection = React.lazy(() => import('./pages/SignupSelection'));
const RegisterClinic = React.lazy(() => import('./pages/RegisterClinic'));
const RegisterEmployer = React.lazy(() => import('./pages/RegisterEmployer'));
const RegisterPatient = React.lazy(() => import('./pages/RegisterPatient'));
const RegisterIndividualPatient = React.lazy(() => import('./pages/RegisterIndividualPatient'));
const RegistrationPending = React.lazy(() => import('./pages/RegistrationPending'));
const FindLocations = React.lazy(() => import('./pages/FindLocations'));
const LocationsPage = React.lazy(() => import('./pages/LocationsPage'));
const IndividualLanding = React.lazy(() => import('./pages/IndividualLanding'));
const ReferralLanding = React.lazy(() => import('./pages/ReferralLanding'));
const SameDayGuaranteeLanding = React.lazy(() => import('./pages/SameDayGuaranteeLanding'));
const GiftMembershipLanding = React.lazy(() => import('./pages/GiftMembershipLanding'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));
const DashboardComparisonPreview = React.lazy(() => import('./pages/DashboardComparisonPreview'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));

// Footer Pages
const Contact = React.lazy(() => import('./pages/Contact'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const HelpCenter = React.lazy(() => import('./pages/HelpCenter'));
const ApiDocs = React.lazy(() => import('./pages/ApiDocs'));
const Status = React.lazy(() => import('./pages/Status'));
const DownloadDocs = React.lazy(() => import('./pages/DownloadDocs'));

// Landing Page Components (to be wrapped)
import PartnerLogos from './components/PartnerLogos';
import VideoSection from './components/VideoSection';
import ProductsSection from './components/ProductsSection';
import WhoWeServe from './components/WhoWeServe';
import TestimonialSection from './components/TestimonialSection';
import ResourcesSection from './components/ResourcesSection';
import CustomerStories from './components/CustomerStories';
import ContactForm from './components/ContactForm';

// Dashboard Pages (Placeholder - will create next)
const AdminDashboard = React.lazy(() => import('./pages/dashboards/AdminDashboard'));
const EmployerDashboard = React.lazy(() => import('./pages/dashboards/EmployerDashboard'));
const ClinicDashboard = React.lazy(() => import('./pages/dashboards/ClinicDashboard'));
const DoctorDashboard = React.lazy(() => import('./pages/dashboards/DoctorDashboard'));
const PatientDashboard = React.lazy(() => import('./pages/dashboards/PatientDashboard'));
const LocationStaffDashboard = React.lazy(() => import('./pages/dashboards/LocationStaffDashboard'));
const SubscriptionSelection = React.lazy(() => import('./pages/SubscriptionSelection'));
const CompleteProfile = React.lazy(() => import('./pages/CompleteProfile'));
const ProfileSettings = React.lazy(() => import('./pages/ProfileSettings'));
const EmployerActivation = React.lazy(() => import('./pages/EmployerActivation'));
const EmployerTeamAccept = React.lazy(() => import('./pages/EmployerTeamAccept'));
const ClinicActivation = React.lazy(() => import('./pages/ClinicActivation'));
const InvoiceView = React.lazy(() => import('./pages/InvoiceView'));
const AzureCallback = React.lazy(() => import('./pages/AzureCallback'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const EmployeeSignup = React.lazy(() => import('./pages/EmployeeSignup'));
const DependentSignup = React.lazy(() => import('./pages/DependentSignup'));
const DependentDashboard = React.lazy(() => import('./pages/dashboards/DependentDashboard'));
const PatientLogin = React.lazy(() => import('./pages/PatientLogin'));
const EmployerLogin = React.lazy(() => import('./pages/EmployerLogin'));
const SmallBusinessLanding = React.lazy(() => import('./pages/SmallBusinessLanding'));
const SelfInsuredLanding = React.lazy(() => import('./pages/SelfInsuredLanding'));
const BrokersLanding     = React.lazy(() => import('./pages/BrokersLanding'));
const InsuredEmployersLanding = React.lazy(() => import('./pages/InsuredEmployersLanding'));

// Referral Pages
const PatientReferralDashboard = React.lazy(() => import('./pages/PatientReferralDashboard'));
const EmployerReferralDashboard = React.lazy(() => import('./pages/EmployerReferralDashboard'));
const ReferralSettings = React.lazy(() => import('./pages/admin/ReferralSettings'));
const ReferralAnalytics = React.lazy(() => import('./pages/admin/ReferralAnalytics'));
const KnowledgeBaseAdmin = React.lazy(() => import('./pages/admin/KnowledgeBaseAdmin')); // Wave 41.19


// New Landing Page (Wave 19 — Manus-inspired employer-facing redesign)
const LandingNavbar      = React.lazy(() => import('./components/landing/LandingNavbar'));
const Hero               = React.lazy(() => import('./components/landing/Hero'));
const StatsBand          = React.lazy(() => import('./components/landing/StatsBand'));
const WhySection         = React.lazy(() => import('./components/landing/WhySection'));
const TrustSection       = React.lazy(() => import('./components/landing/TrustSection'));
const LocationsSection   = React.lazy(() => import('./components/landing/LocationsSection'));
const FamilyOfServices   = React.lazy(() => import('./components/landing/FamilyOfServices'));
const WhoWeServeSection  = React.lazy(() => import('./components/landing/WhoWeServeSection'));
const PricingSection     = React.lazy(() => import('./components/landing/PricingSection'));
const FeatureComparisonTable = React.lazy(() => import('./components/landing/FeatureComparisonTable'));
const ScopeOfServicesSection = React.lazy(() => import('./components/landing/ScopeOfServicesSection'));
const TraditionalVsUsSection = React.lazy(() => import('./components/landing/TraditionalVsUsSection'));
const RealCostSection    = React.lazy(() => import('./components/landing/RealCostSection'));
const TestimonialsSection = React.lazy(() => import('./components/landing/TestimonialsSection'));
const ROICalculator      = React.lazy(() => import('./components/landing/ROICalculator'));
// DemoRequestSection is the target of many CTAs (Hero, FAQ, MemberPrograms).
// Keep it in the main bundle so `href="#request-demo"` always works, even
// before lower-page chunks finish downloading.
import DemoRequestSection from './components/landing/DemoRequestSection';
const ClosingCTA         = React.lazy(() => import('./components/landing/ClosingCTA'));
const LandingFooter      = React.lazy(() => import('./components/landing/LandingFooter'));
const LandingNotInsuranceBanner = React.lazy(() => import('./components/landing/LandingNotInsuranceBanner'));

// Wave 36 — remaining reference-site sections
const RootProblemSection      = React.lazy(() => import('./components/landing/RootProblemSection'));
const DayInLifeMariaSection   = React.lazy(() => import('./components/landing/DayInLifeMariaSection'));
const EnrollmentStepsSection  = React.lazy(() => import('./components/landing/EnrollmentStepsSection'));
// Wave 42.05 — kept on the landing page only; hidden inside dashboards.
const MemberProgramsSection   = React.lazy(() => import('./components/landing/MemberProgramsSection'));
const HSACompatibleSection    = React.lazy(() => import('./components/landing/HSACompatibleSection'));
const FAQSection              = React.lazy(() => import('./components/landing/FAQSection'));
const StatReferencesSection   = React.lazy(() => import('./components/landing/StatReferencesSection'));

// Landing Page Wrapper
const LandingPage = () => {
  // When arriving with a hash (e.g. `/#roi` from segment landing CTAs), lazy-
  // loaded sections mount after initial paint — so browsers can't natively
  // scroll to them. Poll for the target and smooth-scroll once it mounts.
  // Wave 42.331 — re-runs when the hash changes (footer Link nav from same page).
  const location = useLocation();
  React.useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) return;
    let attempts = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        clearInterval(timer);
      } else if (++attempts > 40) {
        clearInterval(timer); // give up after ~4s
      }
    }, 100);
    return () => clearInterval(timer);
  }, [location.hash]);

  return (
    <div className="App font-sans bg-white text-gray-900">
      <LandingNavbar />
      <Hero />
      <StatsBand />
      <div id="why"><WhySection /></div>
      {/* Wave 42.88 — reordered to match /mhc-v3 reference: comparison table
          rises up, WhoWeServe restored, ClosingCTA moves before MemberPrograms. */}
      <TraditionalVsUsSection />
      <div id="about"><TrustSection /></div>
      <div id="locations"><LocationsSection /></div>
      <FamilyOfServices />
      <div id="who-we-serve"><WhoWeServeSection /></div>
      <div id="pricing"><PricingSection /></div>
      {/* Wave 42.92 — standalone HSACompatibleSection removed. HSA callout
          is now inside PricingSection to match the reference layout exactly. */}
      {/* Wave 42.93 — ScopeOfServicesSection replaces FeatureComparisonTable
          to match /mhc-v3 reference (3 vertical plan cards, not a horizontal table). */}
      <ScopeOfServicesSection />
      <RootProblemSection />
      <DayInLifeMariaSection />
      <RealCostSection />
      <TestimonialsSection />
      <div id="roi"><ROICalculator /></div>
      <DemoRequestSection />
      <EnrollmentStepsSection />
      <ClosingCTA />
      <MemberProgramsSection />
      <StatReferencesSection />
      <FAQSection />
      <LandingNotInsuranceBanner />
      <LandingFooter />
    </div>
  );
};

// Loading Component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Unauthorized Page — if user is logged in, offer to take them to their own dashboard
const Unauthorized = () => {
  const stored = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();

  const roleDashboard = {
    admin: '/admin/dashboard',
    clinic_admin: '/clinic/dashboard',
    clinic: '/clinic/dashboard',
    clinic_manager: '/clinic/dashboard',
    clinic_staff: '/staff-dashboard',
    employer: '/employer/dashboard',
    doctor: '/doctor/dashboard',
    patient: '/patient/dashboard',
    dependent: '/dependent/dashboard',
    location_staff: '/staff-dashboard',
    location_manager: '/staff-dashboard',
  };

  const myDashboard = stored?.role ? roleDashboard[stored.role] : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md px-4">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">403</h1>
        <p className="text-xl text-gray-600 mb-2">Unauthorized Access</p>
        {stored && (
          <p className="text-sm text-gray-500 mb-8">
            You&apos;re signed in as <strong>{stored.email || stored.name || stored.role}</strong>,
            which doesn&apos;t have access to this page.
          </p>
        )}
        <div className="flex gap-3 justify-center">
          {myDashboard && (
            <a
              href={myDashboard}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium"
              data-testid="goto-my-dashboard"
            >
              Go to my dashboard
            </a>
          )}
          <a
            href="/login"
            className="px-4 py-2 border border-gray-300 hover:bg-gray-100 text-gray-700 rounded-md font-medium"
            data-testid="goto-login"
          >
            Sign in as different user
          </a>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
      <BrowserRouter>
        <ScrollToTop />
        <MetaPixelTracker />
        <OfflineBanner />
        <SandboxBanner />
        <ImpersonationBanner />
        <React.Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/locations" element={<LocationsPage />} /> {/* Wave 40: static 33-clinic finder */}
            <Route
              path="/individual"
              element={SHOW_INDIVIDUAL ? <IndividualLanding /> : <Navigate to="/" replace />}
            /> {/* Wave 42.171: hidden behind SHOW_INDIVIDUAL flag */}
            <Route path="/referral" element={<ReferralLanding />} /> {/* Wave 40: employer referral $5/employee */}
            <Route path="/same-day-guarantee" element={<SameDayGuaranteeLanding />} />
            <Route path="/gift-membership" element={<GiftMembershipLanding />} />
            <Route path="/faq" element={<FAQPage />} /> {/* Wave 41: FAQ */}
            <Route path="/preview/dashboard-diff" element={<DashboardComparisonPreview />} /> {/* Wave 41.6 QA */}
            <Route path="/find-locations" element={<FindLocations />} /> {/* Legacy patient-facing DB-driven view */}
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/login" element={<Navigate to="/member-login" replace />} />
            <Route path="/common-login" element={<CommonLogin />} />
            <Route path="/staff-login" element={<StaffLogin />} />
            {/* Wave 42.109 — friendly aliases users type without checking docs.
                All three go to the same Staff Login page (SSO or email/password). */}
            <Route path="/clinic-staff" element={<StaffLogin />} />
            <Route path="/clinic-login" element={<StaffLogin />} />
            <Route path="/staff" element={<StaffLogin />} />
            {/* Wave 42.284 — Ambassador standalone auth + portal */}
            <Route path="/ambassador-login" element={<AmbassadorLogin />} />
            <Route path="/ambassador/set-password" element={<AmbassadorSetPassword />} />
            <Route path="/ambassador-portal" element={<AmbassadorPortal />} />
            <Route path="/ambassador-portal/leads" element={
              <AmbassadorPortal><AmbassadorLeadsPage /></AmbassadorPortal>
            } />
            <Route path="/ambassador-portal/blitz" element={
              <AmbassadorPortal><AmbassadorBlitzPage /></AmbassadorPortal>
            } />
            <Route path="/ambassador-portal/calls" element={
              <AmbassadorPortal><BusinessCallsPage /></AmbassadorPortal>
            } />
            <Route path="/ambassador-portal/leaderboard" element={
              <AmbassadorPortal><AmbassadorLeaderboardPage /></AmbassadorPortal>
            } />
            <Route path="/ambassador-portal/resources" element={
              <AmbassadorPortal><AmbassadorResourcesPage /></AmbassadorPortal>
            } />
            <Route path="/patient-login" element={<Navigate to="/member-login" replace />} />
            <Route path="/member-login" element={<PatientLogin />} />
            <Route path="/employer-login" element={<EmployerLogin />} />
            <Route path="/no-insurance" element={<SmallBusinessLanding />} />
            <Route path="/small-business" element={<SmallBusinessLanding />} />
            <Route path="/self-insured" element={<SelfInsuredLanding />} />
            <Route path="/brokers" element={<BrokersLanding />} />
            <Route path="/insured-employers" element={<InsuredEmployersLanding />} />
            <Route
              path="/signup"
              element={SHOW_INDIVIDUAL ? <SignupSelection /> : <Navigate to="/register/employer" replace />}
            />
            <Route path="/register/clinic" element={<RegisterClinic />} />
            <Route path="/register/employer" element={<RegisterEmployer />} />
            <Route path="/register/patient" element={<RegisterPatient />} />
            <Route
              path="/register/individual"
              element={SHOW_INDIVIDUAL ? <RegisterIndividualPatient /> : <Navigate to="/register/employer" replace />}
            />
            <Route path="/registration-pending" element={<RegistrationPending />} />
            <Route path="/complete-profile" element={<CompleteProfile />} />
            <Route path="/employer/activate/:token" element={<EmployerActivation />} />
            <Route path="/employer/team/accept/:token" element={<EmployerTeamAccept />} />
            <Route path="/clinic/activate/:token" element={<ClinicActivation />} />
            <Route path="/invoice/:token" element={<InvoiceView />} />
            <Route path="/auth/azure/complete" element={<AzureCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/signup/employee" element={<EmployeeSignup />} />
            <Route path="/signup/dependent" element={<DependentSignup />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Protected Routes - Admin */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/referrals/settings"
              element={
                <ProtectedRoute allowedRoles={['admin', 'clinic_admin', 'clinic']}>
                  <ReferralSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/referrals/analytics"
              element={
                <ProtectedRoute allowedRoles={['admin', 'clinic_admin', 'clinic']}>
                  <ReferralAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/knowledge-base"
              element={
                <ProtectedRoute allowedRoles={['admin', 'clinic_admin', 'clinic']}>
                  <KnowledgeBaseAdmin />
                </ProtectedRoute>
              }
            /> {/* Wave 41.19 */}

            {/* Protected Routes - Employer */}
            <Route
              path="/employer/dashboard"
              element={
                <ProtectedRoute allowedRoles={['employer']}>
                  <EmployerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employer/referrals"
              element={
                <ProtectedRoute allowedRoles={['employer']}>
                  <EmployerReferralDashboard />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes - Clinic Admin */}
            <Route
              path="/clinic/dashboard"
              element={
                <ProtectedRoute allowedRoles={['clinic_admin', 'clinic', 'clinic_manager', 'clinic_staff']}>
                  <ClinicDashboard />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes - Doctor */}
            <Route
              path="/doctor/dashboard"
              element={
                <ProtectedRoute allowedRoles={['doctor']}>
                  <DoctorDashboard />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes - Location Staff */}
            <Route
              path="/location-staff/dashboard"
              element={
                <ProtectedRoute allowedRoles={['location_staff', 'location_manager']}>
                  <LocationStaffDashboard />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes - Patient */}
            <Route
              path="/patient/subscription"
              element={
                <ProtectedRoute allowedRoles={['patient']}>
                  <SubscriptionSelection />
                </ProtectedRoute>
              }
            />
            
            {/* Payment Success - same component handles success with session_id param */}
            <Route
              path="/patient/subscription/success"
              element={
                <ProtectedRoute allowedRoles={['patient']}>
                  <SubscriptionSelection />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/patient/dashboard"
              element={
                <ProtectedRoute allowedRoles={['patient']}>
                  <PatientDashboard />
                </ProtectedRoute>
              }
            />

            {/* Protected Route - Dependent (read-only) */}
            <Route
              path="/dependent/dashboard"
              element={
                <ProtectedRoute allowedRoles={['dependent']}>
                  <DependentDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/patient/referrals"
              element={
                <ProtectedRoute allowedRoles={['patient']}>
                  <PatientReferralDashboard />
                </ProtectedRoute>
              }
            />

            {/* Profile Settings - All Roles */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute allowedRoles={['admin', 'employer', 'clinic_admin', 'clinic', 'clinic_manager', 'clinic_staff', 'doctor', 'patient', 'location_staff', 'location_manager']}>
                  <ProfileSettings />
                </ProtectedRoute>
              }
            />

            {/* Footer Pages - Public */}
            <Route path="/contact" element={<><Header /><Contact /><Footer /></>} />
            <Route path="/privacy" element={<><Header /><PrivacyPolicy /><Footer /></>} />
            <Route path="/terms" element={<><Header /><TermsOfService /><Footer /></>} />
            <Route path="/help" element={<><Header /><HelpCenter /><Footer /></>} />
            <Route path="/api" element={<><Header /><ApiDocs /><Footer /></>} />
            <Route path="/status" element={<><Header /><Status /><Footer /></>} />
            <Route path="/download-docs" element={<DownloadDocs />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </React.Suspense>
        <Toaster />
        <CookieConsentBanner />
      </BrowserRouter>
      </ConfirmProvider>
    </AuthProvider>
  );
}

export default App;
